import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const geometryTutorials: Record<string, TechniqueBreakdown> = {
  "10101010-0001-400a-a001-000000000010": {
    title: "Angles and Triangles",
    description:
      "Master the fundamental properties of angles, lines, and triangles - the building blocks for all geometric proofs.",
    steps: [
      {
        stepNumber: 1,
        title: "Triangle Angle Sum Theorem",
        explanation:
          "The most important triangle property: the three interior angles of ANY triangle always add up to exactly 180°. This works for all triangles - acute, obtuse, right, equilateral, isosceles, or scalene!",
        tips: [
          "Formula: ∠A + ∠B + ∠C = 180°",
          "To find a missing angle: subtract the two known angles from 180°",
          "Quick check: if your three angles don't add to 180°, something's wrong!",
          "Works even if the triangle looks weird or is very flat",
        ],
        practice: {
          problem:
            "In triangle ABC, angle A = 65° and angle B = 48°. Find angle C.",
          question: "What is angle C?",
          answer: "67",
          hint: "Use: A + B + C = 180°. Solve for C.",
          intermediateSteps: [
            {
              label: "Write equation",
              answer: "65 + 48 + C = 180",
              hint: "Angle sum = 180°",
            },
            {
              label: "Add known angles",
              answer: "113 + C = 180",
              hint: "65 + 48 = 113",
            },
            { label: "Solve for C", answer: "67", hint: "C = 180 - 113 = 67°" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Isosceles Triangle Theorem",
        explanation:
          'In an isosceles triangle (two equal sides), the angles opposite those equal sides are also equal! These are called the "base angles." The remaining angle is the "vertex angle."',
        tips: [
          "Two equal sides → Two equal base angles",
          "If you know the vertex angle, divide the remaining degrees by 2 to get each base angle",
          "If you know one base angle, the other base angle is the same!",
          "Equilateral triangles are special isosceles triangles (all angles = 60°)",
        ],
        practice: {
          problem:
            "Isosceles triangle ABC has AB = AC. The vertex angle at A is 40°. Find the base angles.",
          question: "What is the measure of each base angle?",
          answer: "70",
          hint: "Base angles are equal. Use angle sum theorem.",
          intermediateSteps: [
            {
              label: "Remaining degrees",
              answer: "140",
              hint: "180° - 40° = 140°",
            },
            {
              label: "Each base angle",
              answer: "70",
              hint: "Divide by 2: 140° ÷ 2 = 70°",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Complementary and Supplementary Angles",
        explanation:
          'Complementary angles add to 90° (think "Corner" - right angle). Supplementary angles add to 180° (think "Straight" line). These relationships help solve many angle problems!',
        tips: [
          "Complementary: ∠1 + ∠2 = 90° (forms a right angle)",
          "Supplementary: ∠1 + ∠2 = 180° (forms a straight line)",
          "To find the complement: 90° - angle",
          "To find the supplement: 180° - angle",
        ],
        practice: {
          problem: "Angle A = 55°. Find both the complement and supplement.",
          question: "What is the complement?",
          answer: "35",
          hint: "Complement = 90° - 55°",
          intermediateSteps: [
            { label: "Complement", answer: "35", hint: "90° - 55° = 35°" },
            { label: "Supplement", answer: "125", hint: "180° - 55° = 125°" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Vertical Angles Theorem",
        explanation:
          "When two lines intersect, they form two pairs of opposite angles called vertical angles. Vertical angles are ALWAYS congruent (equal). This is one of the most useful angle relationships!",
        tips: [
          "Vertical angles are across from each other (opposite)",
          "They always have equal measures",
          "Don't confuse with adjacent angles (next to each other)",
          'Forms an "X" pattern where opposite angles match',
        ],
        practice: {
          problem:
            "Two lines intersect forming angles. One angle is 73°. Find the vertical angle.",
          question: "What is the measure of the vertical angle?",
          answer: "73",
          hint: "Vertical angles are equal!",
          intermediateSteps: [
            {
              label: "Vertical angle",
              answer: "73",
              hint: "Vertical angles are congruent: 73°",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Exterior Angle Theorem",
        explanation:
          "An exterior angle of a triangle equals the sum of the two remote (non-adjacent) interior angles. This provides a shortcut for many triangle problems!",
        tips: [
          "Exterior angle = sum of the two remote interior angles",
          "The exterior angle is ALWAYS larger than either remote interior angle",
          "An exterior angle and its adjacent interior angle are supplementary (add to 180°)",
          "Every triangle has 6 exterior angles (2 at each vertex)",
        ],
        practice: {
          problem:
            "Triangle ABC has interior angles A = 52° and B = 63°. Find the exterior angle at C.",
          question: "What is the exterior angle at C?",
          answer: "115",
          hint: "Exterior angle = sum of remote interior angles",
          intermediateSteps: [
            {
              label: "Remote angles",
              answer: "52 and 63",
              hint: "Angles A and B are remote to exterior at C",
            },
            { label: "Sum", answer: "115", hint: "52° + 63° = 115°" },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "In triangle XYZ, angle X = 42° and the exterior angle at Z = 108°. Find angles Y and Z.",
      fullSolution:
        "Use Exterior Angle Theorem: Exterior at Z = X + Y, so 108° = 42° + Y. Therefore Y = 66°. Then use Angle Sum: Z = 180° - 42° - 66° = 72°. Check: Exterior at Z (108°) + Interior Z (72°) = 180° ✓",
    },
  },

  "10101010-0002-400a-a002-000000000010": {
    title: "Similar & Congruent Triangles",
    description:
      "Master the criteria for proving triangles have the same shape (similar) or the same shape AND size (congruent) - the most powerful tools in geometry!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding Congruence vs Similarity",
        explanation:
          "CONGRUENT triangles (≅) are identical - same shape AND same size (like photocopies). SIMILAR triangles (~) have the same shape but different sizes (like a photo and its enlargement). All congruent triangles are similar, but not all similar triangles are congruent!",
        tips: [
          "Congruent (≅): Same angles AND same side lengths",
          "Similar (~): Same angles BUT proportional side lengths",
          'Think: Similar = "looks the same" / Congruent = "is the same"',
          "Scale factor: ratio of corresponding sides in similar triangles",
        ],
        practice: {
          problem:
            "Triangle 1 has sides 3, 4, 5. Triangle 2 has sides 6, 8, 10. Are they congruent or similar?",
          question: "Congruent, similar, or both?",
          answer: "similar",
          hint: "Check if sides are proportional. Are they the same size?",
          intermediateSteps: [
            {
              label: "Check ratios",
              answer: "6/3 = 8/4 = 10/5",
              hint: "All ratios = 2",
            },
            {
              label: "Same shape?",
              answer: "yes",
              hint: "Proportional sides → similar",
            },
            {
              label: "Same size?",
              answer: "no",
              hint: "Different lengths → not congruent",
            },
            {
              label: "Answer",
              answer: "similar",
              hint: "Same shape, different size = similar only",
            },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "The SSS Congruence Postulate",
        explanation:
          "SSS (Side-Side-Side): If all three sides of one triangle match all three sides of another triangle, the triangles are congruent. The angles will automatically match too!",
        tips: [
          "Check all three pairs of corresponding sides",
          "Order matters: match shortest to shortest, longest to longest",
          "If all three sides match exactly, you're done - triangles are ≅",
          "Most reliable method when you have side measurements",
        ],
        practice: {
          problem:
            "Triangle ABC has sides 7, 9, 12. Triangle DEF has sides 7, 9, 12. Are they congruent by SSS?",
          question: "Congruent by SSS? (yes or no)",
          answer: "yes",
          hint: "Compare all three pairs of sides",
          intermediateSteps: [
            {
              label: "First pair",
              answer: "7 = 7",
              hint: "First sides match ✓",
            },
            {
              label: "Second pair",
              answer: "9 = 9",
              hint: "Second sides match ✓",
            },
            {
              label: "Third pair",
              answer: "12 = 12",
              hint: "Third sides match ✓",
            },
            {
              label: "Conclusion",
              answer: "yes",
              hint: "All three sides match → SSS ≅",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "SAS and ASA Congruence",
        explanation:
          "SAS (Side-Angle-Side): Two sides and the INCLUDED angle (the angle between those sides) must match. ASA (Angle-Side-Angle): Two angles and the INCLUDED side (the side between those angles) must match. The included part is crucial!",
        tips: [
          "SAS: The angle MUST be between the two sides",
          "ASA: The side MUST be between the two angles",
          "If the angle/side isn't included, you might have AAS instead (still works!)",
          'Remember: Included = "sandwiched" between',
        ],
        practice: {
          problem:
            "Triangle ABC: AB = 8, ∠B = 60°, BC = 10. Triangle DEF: DE = 8, ∠E = 60°, EF = 10. Congruent?",
          question: "Which congruence theorem? (SSS, SAS, ASA, AAS, or none)",
          answer: "SAS",
          hint: "Two sides and the included angle",
          intermediateSteps: [
            {
              label: "Side 1",
              answer: "AB = DE = 8",
              hint: "First sides match",
            },
            {
              label: "Included angle",
              answer: "∠B = ∠E = 60°",
              hint: "Angles between sides match",
            },
            {
              label: "Side 2",
              answer: "BC = EF = 10",
              hint: "Second sides match",
            },
            {
              label: "Theorem",
              answer: "SAS",
              hint: "Side-Angle-Side congruence",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "AA Similarity Theorem",
        explanation:
          "AA (Angle-Angle): If two angles of one triangle match two angles of another triangle, the triangles are SIMILAR. You only need 2 angles because the third angle is automatically determined (angle sum = 180°).",
        tips: [
          "Only need 2 matching angles to prove similarity",
          "Third angle automatically matches (180° - other two)",
          "Fastest way to prove similarity",
          "After proving similarity, you can use proportional sides",
        ],
        practice: {
          problem:
            "Triangle 1 has angles 53° and 72°. Triangle 2 has angles 53° and 72°. Similar?",
          question: "Are they similar by AA? (yes or no)",
          answer: "yes",
          hint: "Two angles match",
          intermediateSteps: [
            {
              label: "First angle",
              answer: "53° = 53°",
              hint: "First angles match ✓",
            },
            {
              label: "Second angle",
              answer: "72° = 72°",
              hint: "Second angles match ✓",
            },
            {
              label: "Third angle",
              answer: "Both 55°",
              hint: "180° - 53° - 72° = 55° in both",
            },
            {
              label: "Similar?",
              answer: "yes",
              hint: "Two angles match → AA Similarity ✓",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Using Similarity - Proportional Sides",
        explanation:
          "Once you know triangles are similar, their corresponding sides are proportional (have the same ratio). Set up a proportion to find missing sides: a/d = b/e = c/f, where a,b,c are sides of one triangle and d,e,f are corresponding sides of the other.",
        tips: [
          "Write proportion: side1/corresponding side1 = side2/corresponding side2",
          "Cross-multiply to solve: a/b = c/x → ax = bc → x = bc/a",
          "Always match corresponding sides (smallest to smallest, etc.)",
          "Scale factor = ratio of any pair of corresponding sides",
        ],
        practice: {
          problem:
            "Similar triangles. Triangle 1 has sides 4 and 6. Triangle 2 has corresponding side 10 (to the 4). Find the missing side.",
          question: "What is the missing side in Triangle 2?",
          answer: "15",
          hint: "Set up proportion: 4/10 = 6/x",
          intermediateSteps: [
            {
              label: "Scale factor",
              answer: "10/4 = 2.5",
              hint: "10 ÷ 4 = 2.5",
            },
            {
              label: "Multiply",
              answer: "6 × 2.5",
              hint: "Apply scale factor to other side",
            },
            { label: "Answer", answer: "15", hint: "6 × 2.5 = 15" },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "Prove triangles ABC and DEF are congruent: AB = 5, BC = 7, ∠B = 45°, DE = 5, EF = 7, ∠E = 45°. Which theorem?",
      fullSolution:
        "Identify what matches: AB = DE (side), ∠B = ∠E (angle), BC = EF (side). The angle is between the two sides → SAS Congruence. Answer: △ABC ≅ △DEF by SAS",
    },
  },

  "10101010-0003-400a-a003-000000000010": {
    title: "Circle Properties",
    description:
      "Explore the elegant properties of circles including arcs, chords, tangents, and inscribed angles.",
    steps: [
      {
        stepNumber: 1,
        title: "Central vs Inscribed Angles",
        explanation:
          "A CENTRAL angle has its vertex at the center of the circle. An INSCRIBED angle has its vertex on the circle. The key theorem: an inscribed angle is always HALF the central angle that subtends (cuts off) the same arc!",
        tips: [
          "Central angle: vertex at center, measures the arc directly",
          "Inscribed angle: vertex on circle, equals half the arc measure",
          "Formula: Inscribed angle = (1/2) × Central angle",
          "Special case: angle inscribed in semicircle = 90° (because central angle = 180°)",
        ],
        practice: {
          problem:
            "A central angle measures 80°. Find the inscribed angle that subtends the same arc.",
          question: "What is the inscribed angle?",
          answer: "40",
          hint: "Inscribed angle = (1/2) × central angle",
          intermediateSteps: [
            { label: "Central angle", answer: "80°", hint: "Given" },
            {
              label: "Divide by 2",
              answer: "80 ÷ 2",
              hint: "Inscribed = (1/2) central",
            },
            { label: "Inscribed angle", answer: "40°", hint: "80° ÷ 2 = 40°" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "The Inscribed Angle in a Semicircle",
        explanation:
          "Thales' Theorem (one of the oldest theorems!): Any angle inscribed in a semicircle (where one side of the angle is a diameter) is ALWAYS a right angle (90°). This works no matter where on the semicircle you place the vertex!",
        tips: [
          "If one side of the inscribed angle is a diameter → angle = 90°",
          "Diameter creates a central angle of 180°, so inscribed = 180° ÷ 2 = 90°",
          "Great for creating right triangles in circle problems",
          "Works for any point on the semicircle",
        ],
        practice: {
          problem:
            "Points A, B, C lie on a circle. Segment AB is a diameter. What is ∠ACB?",
          question: "What is the measure of angle ACB?",
          answer: "90",
          hint: "Angle inscribed in semicircle theorem",
          intermediateSteps: [
            {
              label: "AB is",
              answer: "diameter",
              hint: "Given: AB is diameter",
            },
            { label: "C is on", answer: "circle", hint: "C is on the circle" },
            {
              label: "Theorem",
              answer: "Inscribed in semicircle",
              hint: "Angle in semicircle = 90°",
            },
            { label: "Answer", answer: "90°", hint: "∠ACB = 90°" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Tangent-Radius Perpendicularity",
        explanation:
          "A tangent line touches a circle at exactly one point. At that point of tangency, the tangent is ALWAYS perpendicular (90°) to the radius. This creates right angles and right triangles that are super useful for solving problems!",
        tips: [
          "Tangent ⊥ Radius at point of tangency (forms 90° angle)",
          "If you draw a radius to the point where a tangent touches, you have a right angle",
          "Creates right triangles: useful for Pythagorean theorem",
          "Two tangents from external point to circle have equal lengths",
        ],
        practice: {
          problem:
            "A tangent touches a circle at point P. A radius is drawn to point P. What angle do they form?",
          question: "What is the angle between the tangent and radius?",
          answer: "90",
          hint: "Tangent-Radius theorem",
          intermediateSteps: [
            {
              label: "Tangent touches at",
              answer: "point P",
              hint: "Point of tangency",
            },
            {
              label: "Radius drawn to",
              answer: "point P",
              hint: "Radius to tangency point",
            },
            { label: "Angle", answer: "90°", hint: "Tangent ⊥ Radius always!" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Arc Measure and Central Angles",
        explanation:
          "The measure of an arc (in degrees) equals the measure of its central angle. A semicircular arc = 180°, a quarter-circle arc = 90°, and a full circle = 360°. Arc lengths are portions of the circumference.",
        tips: [
          "Arc measure (degrees) = Central angle measure",
          "Minor arc: less than 180°",
          "Major arc: more than 180°",
          "Semicircular arc: exactly 180°",
        ],
        practice: {
          problem:
            "A central angle of 60° intercepts an arc. What is the measure of the arc in degrees?",
          question: "What is the arc measure?",
          answer: "60",
          hint: "Arc measure = central angle measure",
          intermediateSteps: [
            {
              label: "Central angle",
              answer: "60°",
              hint: "Given central angle",
            },
            {
              label: "Arc measure",
              answer: "60°",
              hint: "Arc = central angle",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Cyclic Quadrilaterals",
        explanation:
          "A cyclic quadrilateral is a four-sided figure inscribed in a circle (all vertices on the circle). The key property: opposite angles are SUPPLEMENTARY (add to 180°). This gives you powerful angle relationships!",
        tips: [
          "All four vertices must lie on the circle",
          "Opposite angles add to 180°: ∠A + ∠C = 180°, ∠B + ∠D = 180°",
          "If you know one angle, you can find its opposite: 180° - known angle",
          "Rectangles and squares are cyclic quadrilaterals",
        ],
        practice: {
          problem: "In cyclic quadrilateral ABCD, ∠A = 75°. Find ∠C.",
          question: "What is angle C?",
          answer: "105",
          hint: "Opposite angles in cyclic quadrilateral are supplementary",
          intermediateSteps: [
            {
              label: "Relationship",
              answer: "∠A + ∠C = 180°",
              hint: "Opposite angles supplementary",
            },
            {
              label: "Substitute",
              answer: "75° + ∠C = 180°",
              hint: "∠A = 75°",
            },
            { label: "Solve", answer: "∠C = 105°", hint: "180° - 75° = 105°" },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "A circle has radius 5. A tangent from external point P has length 12. Find the distance from P to the center.",
      fullSolution:
        "Tangent-radius forms right angle. Use Pythagorean theorem: d² = r² + tangent². d² = 5² + 12² = 25 + 144 = 169. d = √169 = 13. Distance = 13",
    },
  },

  "10101010-0004-400a-a004-000000000010": {
    title: "Writing a 2-Column Proof",
    description:
      "Learn the formal structure of logical geometric arguments using the two-column proof format.",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding Proof Structure",
        explanation:
          "A 2-column proof is organized like a table with two columns: STATEMENTS (left) show what you're proving step-by-step, and REASONS (right) justify why each statement is true using definitions, postulates, or theorems.",
        tips: [
          "Left column: STATEMENTS (what you claim is true)",
          "Right column: REASONS (why it's true)",
          "Each statement needs a reason, each reason supports a statement",
          "Build logically from given to conclusion",
        ],
        practice: {
          problem: "What goes in the left column of a 2-column proof?",
          question: "Left column contains: statements or reasons?",
          answer: "statements",
          hint: "Think: What are you claiming is true?",
          intermediateSteps: [
            {
              label: "Left column",
              answer: "statements",
              hint: "What you're proving",
            },
            { label: "Right column", answer: "reasons", hint: "Why it's true" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Starting with the Given",
        explanation:
          'Every proof MUST start with the GIVEN information. Your first statement is always what\'s given in the problem, and your first reason is always "Given." This establishes your starting point.',
        tips: [
          "First statement: Copy the given information exactly",
          'First reason: Always write "Given"',
          "Multiple givens? List each as a separate statement",
          "The given is your foundation - everything builds from here",
        ],
        practice: {
          problem:
            "Given: M is the midpoint of segment AB. What are the first statement and reason?",
          question: "What is the first reason in this proof?",
          answer: "given",
          hint: "What's the first reason in every proof?",
          intermediateSteps: [
            {
              label: "First statement",
              answer: "M is midpoint of AB",
              hint: "Copy the given",
            },
            {
              label: "First reason",
              answer: "Given",
              hint: 'Always start with "Given"',
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Using Definitions",
        explanation:
          'Definitions work both ways! If something IS a definition (like "M is a midpoint"), you can use the definition to conclude properties (like "AM ≅ MB"). If something HAS the properties, you can conclude it fits the definition.',
        tips: [
          "Definition of Midpoint: divides segment into two congruent parts",
          "Definition of Angle Bisector: divides angle into two congruent angles",
          "Definition of Perpendicular: forms 90° angles",
          'Definitions are "if and only if" - works both directions',
        ],
        practice: {
          problem:
            "Given: M is midpoint of AB. Prove: AM ≅ MB. What definition do you use?",
          question: "What definition justifies AM ≅ MB?",
          answer: "definition of midpoint",
          hint: "What's the definition of midpoint?",
          intermediateSteps: [
            {
              label: "Statement 1",
              answer: "M is midpoint of AB",
              hint: "Given",
            },
            {
              label: "Statement 2",
              answer: "AM ≅ MB",
              hint: "What midpoint means",
            },
            {
              label: "Reason 2",
              answer: "Definition of Midpoint",
              hint: "Midpoint creates congruent segments",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Applying Postulates and Theorems",
        explanation:
          'Postulates are accepted as true without proof (like "two points determine a line"). Theorems are proven truths you can use (like "vertical angles are congruent"). These are your main reasoning tools!',
        tips: [
          "Common postulates: Segment Addition, Angle Addition, Linear Pair",
          "Common theorems: Vertical Angles, Corresponding Angles, Triangle Angle Sum",
          "Use exact theorem names in your reasons",
          "Theorems must match the situation exactly",
        ],
        practice: {
          problem:
            "Two lines intersect forming ∠1 and ∠3 as vertical angles. Prove: ∠1 ≅ ∠3. What theorem?",
          question: "What theorem proves vertical angles congruent?",
          answer: "vertical angles theorem",
          hint: "What's the name of the theorem about vertical angles?",
          intermediateSteps: [
            {
              label: "Statement",
              answer: "∠1 ≅ ∠3",
              hint: "Vertical angles are equal",
            },
            {
              label: "Reason",
              answer: "Vertical Angles Theorem",
              hint: "Theorem name",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Reaching the Conclusion",
        explanation:
          "Your last statement should match exactly what you were asked to prove. Make sure every step logically follows from previous steps, and that you've built a complete chain from given to conclusion.",
        tips: [
          "Last statement = what you're asked to prove",
          "Check: does each step follow logically from previous steps?",
          "No gaps! Every statement needs justification",
          "Work backwards from conclusion if stuck: what would you need to prove this?",
        ],
        practice: {
          problem:
            "How do you prove triangles congruent using SSS? What must you establish?",
          question: "How many pairs of congruent sides needed for SSS?",
          answer: "3",
          hint: "SSS stands for Side-Side-Side",
          intermediateSteps: [
            {
              label: "First pair",
              answer: "AB ≅ DE",
              hint: "First sides congruent",
            },
            {
              label: "Second pair",
              answer: "BC ≅ EF",
              hint: "Second sides congruent",
            },
            {
              label: "Third pair",
              answer: "CA ≅ FD",
              hint: "Third sides congruent",
            },
            {
              label: "Conclusion",
              answer: "△ABC ≅ △DEF by SSS",
              hint: "All 3 sides → SSS congruence",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Given: ∠1 and ∠2 are vertical angles. Prove: ∠1 ≅ ∠2",
      fullSolution:
        "PROOF:\nStatements | Reasons\n1. ∠1 and ∠2 are vertical angles | Given\n2. ∠1 ≅ ∠2 | Vertical Angles Theorem\n(This is a simple proof - most require more steps!)",
    },
  },
};
